import { useCallback, useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { applyCampaignRadarZero, getCampaignRadarZero } from "services/experimentApi";
import { BENCH_PIPE_OPENING_CM } from "components/calibration/SensorSetupFields";

const cm = (value) => (value == null ? "—" : `${Number(value).toFixed(2)} cm`);
const same = (a, b) => a != null && b != null && Math.abs(a - b) < 0.005;

/**
 * A finished campaign's result: the radar zero it found (how far below the pipe opening the node's radar measures
 * from), how good the level is measured from the opening and with it, and (staff only) one button that makes it the
 * node's radar zero. The backend computes everything (GET/POST campaigns/<id>/radar-zero/).
 *
 * Props: campaignId; deviceName; isStaff.
 */
export default function RadarZeroResult({ campaignId, deviceName, isStaff }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(String(BENCH_PIPE_OPENING_CM));
  const [askedOpening, setAskedOpening] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await getCampaignRadarZero(campaignId, { pipeOpeningCm: askedOpening }));
      setError("");
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [campaignId, askedOpening]);
  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p role="alert" className="text-xs text-red-700">{error}</p>;
  if (!data) return null;
  const result = data.result || {};
  const current = data.device?.radar_zero_cm ?? null;
  const found = result.radar_zero_cm;
  const applied = same(current, found);

  const apply = async () => {
    setBusy(true);
    setMessage("");
    try {
      const response = await applyCampaignRadarZero(campaignId, { expected: current, note, pipeOpeningCm: askedOpening });
      setData((previous) => ({ ...previous, device: response.device }));
      setConfirming(false);
      setMessage(`Applied: new readings of ${deviceName || "this node"} use a radar zero of ${cm(found)}.`);
    } catch (requestError) {
      setMessage(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  if (result.reason === "pipe_opening_missing") {
    const value = Number(opening);
    return (
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="radar-zero-result">
        <p className="w-full">
          This campaign ran before the pipe opening was recorded. Give the bench's pipe opening to ground to work out
          its radar zero.
        </p>
        <label className="flex flex-col gap-1">
          <span>Pipe opening to ground (cm)</span>
          <input type="number" step="0.1" value={opening} onChange={(event) => setOpening(event.target.value)} className="w-24 rounded border border-amber-300 px-2 py-1" />
        </label>
        <button
          type="button"
          disabled={!(value > 0)}
          onClick={() => setAskedOpening(value)}
          className="rounded bg-amber-700 px-3 py-1.5 font-semibold text-white disabled:opacity-50"
        >
          Work it out
        </button>
      </div>
    );
  }
  if (result.reason) {
    return (
      <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="radar-zero-result">
        No radar zero from this campaign: {result.message}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-emerald-200 bg-white p-3 text-sm" data-testid="radar-zero-result">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Radar zero found</span>
        <span className="text-2xl font-bold text-slate-900" data-testid="radar-zero-found">{cm(found)}</span>
        <span className="text-xs text-slate-500">
          below the pipe opening, from {result.pairs} levels (spread {cm(result.spread_cm)}), pipe opening {cm(result.pipe_opening_cm)}
          {result.pipe_opening_source === "given" ? " (given)" : ""}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg bg-slate-50 p-2">
          <span className="block text-slate-500">Not calibrated</span>
          <span className="font-semibold text-slate-800">
            typical error {cm(result.not_calibrated?.rms_cm)}, worst {cm(result.not_calibrated?.max_cm)}
          </span>
        </div>
        <div className="rounded-lg bg-emerald-50 p-2">
          <span className="block text-emerald-700">Calibrated</span>
          <span className="font-semibold text-emerald-900">
            typical error {cm(result.calibrated?.rms_cm)}, worst {cm(result.calibrated?.max_cm)}
          </span>
        </div>
      </div>
      <p className="text-xs text-slate-600">
        {deviceName || "This node"}'s radar zero now: <strong>{current == null ? "not set" : cm(current)}</strong>
      </p>
      {applied ? (
        <p className="flex items-center gap-1 text-xs font-semibold text-emerald-800">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Applied
        </p>
      ) : isStaff ? (
        confirming ? (
          <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" role="dialog" aria-label="Confirm radar zero">
            <p>
              Change {deviceName || "this node"}'s radar zero from <strong>{current == null ? "not set" : cm(current)}</strong> to{" "}
              <strong>{cm(found)}</strong>? Every new reading uses it; older readings stay as they were.
            </p>
            <input
              type="text"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Note (optional)"
              aria-label="Note"
              className="rounded border border-amber-300 px-2 py-1"
            />
            <div className="flex gap-2">
              <button type="button" disabled={busy} onClick={apply} className="rounded bg-emerald-700 px-3 py-1.5 font-semibold text-white disabled:opacity-50">
                Yes, apply
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirming(false)} className="rounded border border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-700">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="self-start rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white"
          >
            Apply radar zero to {deviceName || "this node"}
          </button>
        )
      ) : (
        <p className="text-xs text-slate-500">A staff account applies it to the node.</p>
      )}
      {message && <p className="text-xs text-slate-700" role="status">{message}</p>}
    </div>
  );
}
